import React, { useEffect, useState } from "react";
import { Coffee, Plus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState, fmtDate } from "../components/ui";

export default function CompOff() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ workedDate: "", reason: "", days: 1 });
  const [busy, setBusy] = useState(false);
  const canDecide = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);

  const load = () => api.get("/compoff").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/compoff", form); setOpen(false); setForm({ workedDate: "", reason: "", days: 1 }); load(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const decide = async (id, status) => { try { await api.put(`/compoff/${id}/decide`, { status }); load(); } catch (e) { window.alert(apiError(e.response?.data?.detail)); } };
  if (!rows) return <Loading />;

  return (
    <div>
      <PageHeader title="Comp-off" subtitle="Compensatory leave for working on holidays/weekends">
        <button data-testid="request-compoff" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Request Comp-off</button>
      </PageHeader>
      {rows.length === 0 ? <EmptyState icon={Coffee} title="No comp-off requests" hint="Worked on a holiday? Request comp-off to earn a leave day." /> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full" data-testid="compoff-table">
            <thead><tr><th className="th">Employee</th><th className="th">Worked On</th><th className="th">Days</th><th className="th">Reason</th><th className="th">Status</th>{canDecide && <th className="th">Action</th>}</tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`compoff-${r.id}`}>
                  <td className="td font-medium text-slate-800 dark:text-slate-100">{r.employeeName}</td>
                  <td className="td text-sm">{fmtDate(r.workedDate)}</td>
                  <td className="td font-mono text-sm">{r.days}</td>
                  <td className="td max-w-[240px] truncate text-sm text-slate-500">{r.reason}</td>
                  <td className="td"><Badge value={r.status} /></td>
                  {canDecide && <td className="td">{r.status === "PENDING" ? (
                    <div className="flex gap-1.5">
                      <button data-testid={`compoff-approve-${r.id}`} onClick={() => decide(r.id, "APPROVED")} className="rounded bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15">Approve</button>
                      <button data-testid={`compoff-reject-${r.id}`} onClick={() => decide(r.id, "REJECTED")} className="rounded bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-500/15">Reject</button>
                    </div>
                  ) : <span className="text-xs text-slate-400">—</span>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div></Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Request Comp-off"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="co-form" data-testid="co-submit" disabled={busy} className="btn-accent">Submit</button></>}>
        <form id="co-form" onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Worked Date *</label><input required type="date" data-testid="co-date" value={form.workedDate} onChange={(e) => setForm({ ...form, workedDate: e.target.value })} className="input" /></div>
            <div><label className="label">Days</label><select value={form.days} onChange={(e) => setForm({ ...form, days: +e.target.value })} className="input"><option value={0.5}>0.5</option><option value={1}>1</option></select></div>
          </div>
          <div><label className="label">Reason *</label><textarea required data-testid="co-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="input h-20 py-2" placeholder="e.g. Worked on a public holiday for go-live" /></div>
        </form>
      </Modal>
    </div>
  );
}
